import { MultiSelect } from "primereact/multiselect";
import { StatusIcon } from "../../pages/specialty-assistant/dashboard/StatusIcon";
import * as client from "../../client";
import { ReportStatus } from "../../api/types";
import { InUseDecoration } from "../in-use-decoration/InUseDecoration";
import styles from "./Filters.module.css";
import { PantheonAvatarWithoutMenu } from "../avatar/Avatar";
import { useUsers } from "../../pages/specialty-assistant/data";
import { displayNameForUser } from "../../util";

type StatusOption = {
  name: string;
  code: ReportStatus;
};

type StatusFilterDropdownProps = {
  statuses: StatusOption[];
  selectedStatuses: StatusOption[];
  setSelectedStatuses: (statuses: StatusOption[]) => void;
  isTransparent?: boolean;
};

export const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  statuses,
  selectedStatuses,
  setSelectedStatuses,
  isTransparent = true,
}) => {
  return (
    <MultiSelect
      value={selectedStatuses}
      scrollHeight="600px"
      onChange={(e) => setSelectedStatuses(e.value)}
      itemTemplate={(status: StatusOption) => (
        <StatusIcon status={status.code as client.status} />
      )}
      options={statuses}
      optionLabel="name"
      placeholder={""}
      maxSelectedLabels={3}
      className={
        isTransparent
          ? styles.multiSelectTransparent
          : styles.multiSelectFixedWidth
      }
      dropdownIcon={() => (
        <div className={styles.decorationWrapper}>
          <span className="pi pi-filter"></span>
          {selectedStatuses.length > 0 ? (
            <InUseDecoration count={selectedStatuses.length} />
          ) : null}
        </div>
      )}
    />
  );
};

export const UNASSIGNED_USER: UserOption = {
  name: "Unassigned",
  id: "Unassigned",
};

export type UserOption = { name: string; id: string };

type AssigneeFilterDropdownProps = {
  icon: string;
  selectedUsers: UserOption[];
  setSelectedUsers: (ids: UserOption[]) => void;
  isTransparent?: boolean;
};

const avatarTemplate = ({ name }: { name: string }) => {
  return (
    <PantheonAvatarWithoutMenu
      label={name === "Unassigned" ? undefined : name}
      showName={true}
    />
  );
};

export const AssigneeFilterDropdown: React.FC<AssigneeFilterDropdownProps> = ({
  icon,
  selectedUsers,
  setSelectedUsers,
  isTransparent = true,
}) => {
  const users = useUsers();
  const options = [
    ...(users?.data?.all_company_users ?? []).map((u) => ({
      name: displayNameForUser(u),
      id: u.id,
    })),
    { name: "Unassigned", id: "Unassigned" },
  ];

  return (
    <MultiSelect
      value={selectedUsers}
      onChange={(e) => setSelectedUsers(e.value)}
      options={options}
      itemTemplate={avatarTemplate}
      optionLabel="name"
      placeholder={""}
      scrollHeight="600px"
      className={
        isTransparent
          ? styles.multiSelectTransparent
          : styles.multiSelectFixedWidth
      }
      dropdownIcon={() => (
        <div className={styles.decorationWrapper}>
          <span className={icon}></span>
          {selectedUsers.length > 0 ? (
            <InUseDecoration count={selectedUsers.length} />
          ) : null}
        </div>
      )}
    />
  );
};

export type FilterOption<Code> = {
  name: string;
  code: Code;
};

type FilterProps<Code> = {
  options: FilterOption<Code>[];
  selectedOptions: FilterOption<Code>[];
  setSelectedOptions: (option: FilterOption<Code>[]) => void;
  isTransparent?: boolean;
  itemTemplate?: (option: FilterOption<Code>) => React.ReactNode;
};

export function FilterDropdown<Code>({
  options,
  selectedOptions,
  setSelectedOptions,
  isTransparent = true,
  itemTemplate,
}: FilterProps<Code>) {
  return (
    <MultiSelect
      value={selectedOptions}
      onChange={(e) => setSelectedOptions(e.value)}
      options={options}
      scrollHeight="600px"
      optionLabel="name"
      placeholder={""}
      maxSelectedLabels={3}
      className={
        isTransparent
          ? styles.multiSelectTransparent
          : styles.multiSelectFixedWidth
      }
      itemTemplate={itemTemplate}
      dropdownIcon={() => (
        <div className={styles.decorationWrapper}>
          <span className="pi pi-filter"></span>
          {selectedOptions.length > 0 ? (
            <InUseDecoration count={selectedOptions.length} />
          ) : null}
        </div>
      )}
    />
  );
}
